import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ChevronRight, HandCoins, Users2, BookOpen, CheckCircle2, Info } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

const ICONS = {
  "youth-dhamma":         { Icon: Users2,   tint: "bg-saffron/10 text-saffron border-saffron/30" },
  "sangha-mutual-aid":    { Icon: HandCoins, tint: "bg-rose-500/10 text-rose-700 border-rose-500/30" },
  "dhamma-pad-microgrant":{ Icon: BookOpen, tint: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30" },
};

export default function Grants() {
  const [programmes, setProgrammes] = useState([]);

  useEffect(() => {
    api.get("/grants/programmes").then((r) => setProgrammes(r.data.programmes || [])).catch(() => {});
  }, []);

  return (
    <section className="bg-pearl min-h-[calc(100vh-5rem)] py-14" data-testid="grants-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy mb-5">
          <ArrowLeft size={14} /> Back to home
        </Link>
        <div className="flex items-center gap-3 flex-wrap">
          <p className="text-[10px] uppercase tracking-[0.25em] text-saffron">Charity grants</p>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-500/15 text-amber-700 border border-amber-500/30 rounded-sm text-[10px] uppercase tracking-widest font-bold" data-testid="grants-coming-soon-badge">
            Coming soon
          </span>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl text-navy mt-2">Grant programmes</h1>
        <p className="mt-3 max-w-2xl text-navy/70">
          ABGN-UK is preparing to launch three small-grant programmes for
          community-led Buddhist and anti-caste projects, plus confidential
          hardship support for members. We are still building our reserves
          and finalising the Trustee panel process. Applications will open
          publicly once our funding pool reaches the agreed threshold.
        </p>
        <div className="mt-4 max-w-2xl text-sm text-navy/60 bg-white border border-amber-200/60 rounded-md p-4 flex items-start gap-3">
          <Info size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <strong className="text-navy/80">Why "coming soon"?</strong> A community grant programme must
            be financially sustainable before its first cycle. We are publishing
            the eligibility, cycle, and amount caps now so that you can plan
            your project — and so the community can hold us accountable to
            launch on time. Sign up to our newsletter to be the first to hear when applications open.
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-5">
          {programmes.map((p) => {
            const meta = ICONS[p.id] || { Icon: HandCoins, tint: "bg-navy/5 text-navy border-navy/15" };
            const Icon = meta.Icon;
            return (
              <article key={p.id} className="bg-white border border-navy/10 rounded-xl p-6 flex flex-col relative overflow-hidden" data-testid={`grant-prog-${p.id}`}>
                <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] uppercase tracking-widest rounded-sm border self-start ${meta.tint}`}>
                  <Icon size={12} /> Up to £{p.max_award}
                </span>
                <h2 className="font-display text-xl text-navy mt-3 leading-tight">{p.title}</h2>
                <p className="text-sm text-navy/65 mt-1">{p.tagline}</p>
                <div className="mt-3 text-xs text-navy/55">
                  <strong className="text-navy/75">Cycle:</strong> {p.cycle}
                </div>
                <ul className="mt-3 space-y-1.5 text-sm text-navy/75">
                  {(p.what_we_fund || []).slice(0, 3).map((f, i) => (
                    <li key={i} className="flex items-start gap-1.5">
                      <CheckCircle2 size={13} className="mt-0.5 text-emerald-700 flex-shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <button
                  disabled
                  data-testid={`grant-cs-${p.id}`}
                  className="mt-5 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 bg-pearl border border-navy/15 text-navy/55 rounded-md text-sm cursor-not-allowed"
                  title="This programme is not yet accepting applications"
                >
                  Coming soon
                </button>
              </article>
            );
          })}
        </div>

        <div className="mt-12 bg-white border border-navy/10 rounded-xl p-6 sm:p-8" data-testid="grants-transparency">
          <h2 className="font-display text-2xl text-navy flex items-center gap-2"><Info size={18} className="text-saffron" /> How decisions will be made (when applications open)</h2>
          <ol className="mt-4 space-y-2 text-sm text-navy/75 list-decimal pl-5">
            <li>Applications will be received continuously and acknowledged within 5 working days.</li>
            <li>The Trustee panel will score each application against the published eligibility for that programme.</li>
            <li>Successful applicants will be notified and asked for proof of identity & bank details. Disbursement will happen within 14 days of notification.</li>
            <li>Recipients will commit to a short, public-facing impact note 6 months after award (anonymised by request for hardship support).</li>
            <li>Aggregate award totals will be published on our annual impact report — see the Charity Transparency & Governance page.</li>
          </ol>
        </div>
      </div>
    </section>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white border border-navy/10 rounded-md p-4">
      <div className="text-[10px] uppercase tracking-widest text-navy/55">{label}</div>
      <div className="mt-1 font-display text-2xl text-navy">{value ?? 0}</div>
    </div>
  );
}

function ApplyModal({ programme, onClose }) {
  const [form, setForm] = useState({
    programme_id: programme.id,
    applicant_name: "", applicant_email: "", applicant_phone: "",
    organisation: "",
    project_title: "",
    project_summary: "",
    amount_requested: Math.min(programme.max_award, 150),
    timeline: "",
    beneficiaries_count: "",
    consent_share_publicly: false,
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.amount_requested > programme.max_award) {
      toast.error(`Maximum award is £${programme.max_award} for this programme.`);
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        amount_requested: parseFloat(form.amount_requested),
        beneficiaries_count: form.beneficiaries_count ? parseInt(form.beneficiaries_count, 10) : undefined,
      };
      const r = await api.post("/grants/apply", payload);
      setDone(r.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-navy/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="grant-apply-modal">
      <div className="bg-pearl rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden max-h-[92vh] flex flex-col">
        <div className="px-5 py-4 border-b border-navy/10 flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-saffron">Apply · up to £{programme.max_award}</div>
            <div className="font-display text-xl text-navy mt-0.5">{programme.title}</div>
          </div>
          <button type="button" onClick={onClose} className="text-navy/50 hover:text-navy text-sm">Close</button>
        </div>

        {done ? (
          <div className="p-8 text-center" data-testid="grant-apply-success">
            <CheckCircle2 size={40} className="mx-auto text-emerald-600" />
            <h3 className="font-display text-2xl text-navy mt-3">Application received</h3>
            <p className="text-navy/65 mt-2">
              Reference: <code className="font-mono text-saffron">{done.application_id}</code>
            </p>
            <p className="text-navy/65 mt-1 text-sm">
              You'll hear from the Trustee panel within 5 working days at {form.applicant_email}.
            </p>
            <button onClick={onClose} className="mt-6 px-5 py-2 bg-navy text-pearl rounded-md text-sm">Done</button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-5 space-y-4 overflow-y-auto">
            <div className="bg-pearl/60 border border-navy/10 rounded p-3 text-xs text-navy/65">
              <strong className="text-navy/80">Eligibility for this programme:</strong>
              <ul className="mt-1 list-disc pl-4 space-y-0.5">
                {programme.eligibility.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Label text="Your name">
                <input required maxLength={120} value={form.applicant_name} onChange={(e) => upd("applicant_name", e.target.value)} className="form-input w-full" data-testid="grant-name" />
              </Label>
              <Label text="Email">
                <input required type="email" value={form.applicant_email} onChange={(e) => upd("applicant_email", e.target.value)} className="form-input w-full" data-testid="grant-email" />
              </Label>
              <Label text="Phone (optional)">
                <input maxLength={40} value={form.applicant_phone} onChange={(e) => upd("applicant_phone", e.target.value)} className="form-input w-full" />
              </Label>
              <Label text="Organisation (if any)">
                <input maxLength={200} value={form.organisation} onChange={(e) => upd("organisation", e.target.value)} className="form-input w-full" />
              </Label>
            </div>
            <Label text="Project / request title">
              <input required maxLength={200} value={form.project_title} onChange={(e) => upd("project_title", e.target.value)} className="form-input w-full" data-testid="grant-title" />
            </Label>
            <Label text="What you'll do (200–400 words)">
              <textarea required minLength={20} maxLength={4000} rows={6} value={form.project_summary} onChange={(e) => upd("project_summary", e.target.value)} className="form-input w-full font-serif" data-testid="grant-summary" />
            </Label>
            <div className="grid sm:grid-cols-3 gap-3">
              <Label text={`Amount (£, max ${programme.max_award})`}>
                <input required type="number" min={10} max={programme.max_award} step="1" value={form.amount_requested} onChange={(e) => upd("amount_requested", e.target.value)} className="form-input w-full" data-testid="grant-amount" />
              </Label>
              <Label text="Beneficiaries (people reached)">
                <input type="number" min={1} value={form.beneficiaries_count} onChange={(e) => upd("beneficiaries_count", e.target.value)} className="form-input w-full" />
              </Label>
              <Label text="Timeline">
                <input maxLength={400} value={form.timeline} onChange={(e) => upd("timeline", e.target.value)} placeholder="e.g. Aug–Oct 2026" className="form-input w-full" />
              </Label>
            </div>
            <label className="flex items-start gap-2 text-sm text-navy/75">
              <input type="checkbox" checked={form.consent_share_publicly} onChange={(e) => upd("consent_share_publicly", e.target.checked)} className="mt-1" />
              <span>I consent to ABGN-UK sharing my project (anonymised on request) in the annual impact report.</span>
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={onClose} className="px-4 py-2 text-navy/65 text-sm">Cancel</button>
              <button type="submit" disabled={busy} className="px-5 py-2 bg-navy text-pearl rounded-md text-sm hover:bg-navy-light disabled:opacity-40" data-testid="grant-submit">
                {busy ? "Submitting…" : "Submit application"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function Label({ text, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-widest text-navy/60 mb-1">{text}</span>
      {children}
    </label>
  );
}
